// Whiteboard — Export: a high-resolution PNG or a vector PDF of the whole
// board, of what is selected, or of one marked-up drawing (a picture or PDF
// page and what is drawn on it, cropped to its edges; a PDF's pages can go
// out together, one page each). Every export carries a footer: the site's
// logo, the work item, and when and by whom it was exported.

import React, { useMemo, useState } from 'react';
import { Icon } from '@modules/database/whiteboard/frontend/ui';
import {
  exportBox, pngSize, maxScale, exportPng, exportPdf, exportPdfSheets, pdfImages, safeFileName, download, loadLogo,
  PAGE_SIZES, FOOTER_H, drawingsOf, drawingLabel, drawingFrame, markedUp, paperOf,
} from '@modules/database/whiteboard/frontend/engine/export';

const PNG_SCALES = [
  { v: 1, l: 'Screen', note: '96 dpi' },
  { v: 2, l: 'High', note: '192 dpi' },
  { v: 4, l: 'Very high', note: '384 dpi' },
  { v: 8, l: 'Print', note: '768 dpi' },
];
const PAGES = [['board', 'Board size'], ['A4', 'A4'], ['A3', 'A3'], ['A2', 'A2'], ['A1', 'A1'], ['Letter', 'Letter']];

function Seg({ value, options, onChange, label }) {
  return (
    <div className="wb-seg wb-seg-wide" role="radiogroup" aria-label={label}>
      {options.map(([v, l, disabled]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} disabled={disabled}
          className={`wb-seg-btn ${value === v ? 'is-active' : ''}`} onClick={() => onChange(v)}>{l}</button>
      ))}
    </div>
  );
}

const stem = (name) => String(name || '').replace(/\.pdf$/i, '');

// drawingId: open on that drawing (the one in focus)
export default function ExportDialog({ elements, selected, files, workItem, fieldName, me, logo, drawingId, onClose }) {
  const drawings = useMemo(() => drawingsOf(elements), [elements]);
  const [start] = useState(() => {
    if (drawingId && drawings.some((d) => d.id === drawingId)) return drawingId;
    if (selected.length === 1 && selected[0].type === 'image') return selected[0].id;
    return null;
  });
  const [format, setFormat] = useState('png');
  const [area, setArea] = useState(start ? 'drawing' : 'board');
  const [drawing, setDrawing] = useState(start || drawings[0]?.id || null);
  const [sheets, setSheets] = useState('one');     // a PDF's pages: 'one' | 'all'
  const [scale, setScale] = useState(4);
  const [bg, setBg] = useState('white');
  const [page, setPage] = useState('A3');
  const [sheetPage, setSheetPage] = useState('sheet');
  const [orientation, setOrientation] = useState('auto');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const dEl = area === 'drawing' ? drawings.find((d) => d.id === drawing) || null : null;
  const docPages = dEl?.doc ? drawings.filter((d) => d.doc && d.doc.fileId === dEl.doc.fileId) : [];
  const allPages = format === 'pdf' && sheets === 'all' && docPages.length > 1;
  const list = useMemo(() => {
    if (area === 'selection' && selected.length) return selected;
    if (area === 'drawing') return dEl ? markedUp(elements, dEl) : [];
    return elements;
  }, [area, selected, elements, dEl]);
  const box = useMemo(() => (dEl ? drawingFrame(dEl) : exportBox(list)), [dEl, list]);
  const cap = box ? maxScale(box) : 1;
  const paper = dEl ? paperOf(box.w, box.h) : null;
  const pdfPage = dEl ? sheetPage : page;
  const today = new Date().toLocaleDateString('en-CA');   // YYYY-MM-DD, the local day
  const what = dEl
    ? (dEl.doc ? `${stem(dEl.doc.name)}${dEl.doc.pages > 1 && !allPages ? ` p${dEl.doc.page}` : ''}` : `${fieldName || 'Whiteboard'} ${drawingLabel(dEl, drawings).toLowerCase()}`) + ' marked up'
    : (fieldName || 'Whiteboard');
  const base = safeFileName(`${workItem?.key || ''} ${what} ${today}`);

  async function run() {
    setBusy(true); setErr(null);
    try {
      const when = new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
      const logoImg = await loadLogo(logo);
      const footerFor = (d) => ({
        logo: logoImg,
        left: [workItem?.key, workItem?.summary].filter(Boolean).join(' · ') || (fieldName || 'Whiteboard'),
        right: `${d ? `${drawingLabel(d, drawings)} · ` : ''}${fieldName || 'Whiteboard'} · exported ${when}${me ? ` by ${me}` : ''}`,
      });
      if (format === 'png') {
        const images = new Map();
        for (const el of list) {
          if (el.type !== 'image') continue;
          const f = files.get(el.fileId);
          if (f?.img) images.set(el.fileId, f.img);
        }
        const blob = await exportPng(list, images, { scale: Math.min(scale, cap), background: bg === 'white' ? '#ffffff' : 'transparent', footer: footerFor(dEl), frame: dEl ? box : null });
        download(blob, `${base}.png`);
      } else if (dEl) {
        // one page per drawing page, each cropped to its own edges
        const sheetList = (allPages ? docPages : [dEl]).map((d) => ({ elements: markedUp(elements, d), frame: drawingFrame(d), footer: footerFor(d) }));
        const seen = new Map();
        for (const sh of sheetList) for (const el of sh.elements) seen.set(el.id, el);
        const pics = await pdfImages([...seen.values()], files.files);
        const { blob } = await exportPdfSheets(sheetList, pics, { page: pdfPage, orientation, title: `${workItem?.key || ''} ${what}`.trim() });
        download(blob, `${base}.pdf`);
      } else {
        const pics = await pdfImages(list, files.files);
        const { blob } = await exportPdf(list, pics, { page, orientation, footer: footerFor(null), title: `${workItem?.key || ''} ${fieldName || 'Whiteboard'}`.trim() });
        download(blob, `${base}.pdf`);
      }
      onClose();
    } catch (e) {
      setErr(e?.message || 'The export failed.');
    } finally { setBusy(false); }
  }

  const px = box ? pngSize({ ...box, h: box.h + FOOTER_H }, Math.min(scale, cap)) : null;
  const pageOptions = dEl
    ? [['sheet', paper ? `${paper} (its sheet)` : 'Its own size'], ...PAGES.filter(([v]) => v !== 'board' && v !== paper)]
    : PAGES;
  return (
    <div className="wb-modal-scrim" onPointerDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="wb-modal" role="dialog" aria-modal="true" aria-label="Export the whiteboard" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
        <div className="wb-modal-head">
          <div>
            <div className="wb-modal-title">Export</div>
            <div className="wb-modal-sub">{fieldName}{workItem?.key ? ` · ${workItem.key}` : ''}</div>
          </div>
          <button type="button" className="wb-btn" aria-label="Close" onClick={onClose}><Icon name="close" /></button>
        </div>
        <div className="wb-form">
          <div className="wb-form-row"><span>Format</span>
            <Seg label="Format" value={format} onChange={setFormat} options={[['png', 'PNG picture'], ['pdf', 'PDF (vector)']]} />
          </div>
          <div className="wb-form-row"><span>What</span>
            <Seg label="What" value={area} onChange={setArea} options={[
              ['board', 'Whole board'],
              ['drawing', 'Marked-up drawing', !drawings.length],
              ['selection', `Selection${selected.length ? ` (${selected.length})` : ''}`, !selected.length],
            ]} />
          </div>
          {area === 'drawing' && dEl && (
            <>
              <div className="wb-form-row"><span>Drawing</span>
                {drawings.length > 1 ? (
                  <select className="wb-input wb-export-pick" value={drawing} onChange={(e) => setDrawing(e.target.value)} aria-label="Drawing">
                    {drawings.map((d) => <option key={d.id} value={d.id}>{drawingLabel(d, drawings)}</option>)}
                  </select>
                ) : <span className="wb-export-name"><Icon name={dEl.doc ? 'pdf' : 'image'} size={16} />{drawingLabel(dEl, drawings)}</span>}
              </div>
              {format === 'pdf' && docPages.length > 1 && (
                <div className="wb-form-row"><span>Pages</span>
                  <Seg label="Pages" value={sheets} onChange={setSheets} options={[['one', 'This page'], ['all', `All ${docPages.length} pages on the board`]]} />
                </div>
              )}
              <div className="wb-form-note">Only the drawing and what is marked up on it — anything past its edges is left out.</div>
            </>
          )}
          {format === 'png' ? (
            <>
              <div className="wb-form-row"><span>Resolution</span>
                <Seg label="Resolution" value={scale} onChange={setScale} options={PNG_SCALES.map((s) => [s.v, s.l, s.v > cap * 1.0001 && s.v !== 1])} />
              </div>
              <div className="wb-form-note">
                {px ? <>{px.w.toLocaleString('en-ZA')} × {px.h.toLocaleString('en-ZA')} pixels · {PNG_SCALES.find((s) => s.v === scale)?.note}</> : 'The board is empty.'}
                {box && scale > cap && <> · this board is large, so the picture is drawn at the most a browser allows</>}
              </div>
              <div className="wb-form-row"><span>Background</span>
                <Seg label="Background" value={bg} onChange={setBg} options={[['white', 'White'], ['clear', 'See-through']]} />
              </div>
            </>
          ) : (
            <>
              <div className="wb-form-row"><span>Page</span>
                {dEl
                  ? <Seg label="Page" value={sheetPage} onChange={setSheetPage} options={pageOptions} />
                  : <Seg label="Page" value={page} onChange={setPage} options={pageOptions} />}
              </div>
              {pdfPage !== 'board' && pdfPage !== 'sheet' && (
                <div className="wb-form-row"><span>Orientation</span>
                  <Seg label="Orientation" value={orientation} onChange={setOrientation} options={[['auto', 'Best fit'], ['portrait', 'Portrait'], ['landscape', 'Landscape']]} />
                </div>
              )}
              <div className="wb-form-note">
                Lines, shapes and words stay sharp at any zoom; pictures keep their full resolution.
                {pdfPage === 'sheet'
                  ? (paper ? ` Printed on ${paper}, the drawing's own sheet size, with the footer under it.` : ' One page, the size of the drawing.')
                  : pdfPage !== 'board' && PAGE_SIZES[pdfPage] ? ` ${dEl ? 'The drawing' : 'The board'} is scaled to fit the page.` : ' One page, the size of the board.'}
              </div>
            </>
          )}
          <div className="wb-export-foot">
            {logo && <img src={logo} alt="" />}
            <span>Every export carries this site's logo, the work item and the date in its footer.</span>
          </div>
          {err && <div className="wb-form-err">{err}</div>}
        </div>
        <div className="wb-modal-foot">
          <button type="button" className="wb-text-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="wb-primary" onClick={run} disabled={busy || !box || (area === 'drawing' && !dEl)}>
            <Icon name="download" size={16} />{busy ? 'Exporting…' : `Export ${format.toUpperCase()}${allPages ? ` (${docPages.length} pages)` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
